// Admin › Feedback: the words, glyphs and chips the list and the detail page
// share, and the list view as it is kept in the address bar.
//
// Status reads as a dot and its word, severity as a word coloured only when it
// needs attention: never colour alone. Colours keep the portal's slate/cyan
// palette; red is kept for critical reports, the one thing here that needs
// attention now. One badge only, for who a report waits on.

import type { ComponentType, SVGProps } from 'react';
import {
  ArchiveBoxIcon,
  BugAntIcon,
  ChatBubbleLeftEllipsisIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InboxIcon,
  LightBulbIcon,
  MinusCircleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { formatDate } from '../../utils/formatters';
import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_STATUSES,
  type FeedbackCategory,
  type FeedbackCloseReason,
  type FeedbackCounts,
  type FeedbackPerson,
  type FeedbackRow,
  type FeedbackSeverity,
  type FeedbackStatus,
} from '../../types/feedback';

type Glyph = ComponentType<SVGProps<SVGSVGElement>>;

interface ChipLook {
  label: string;
  Icon: Glyph;
  /** Ground and text for the chip; text stays high contrast on the dark page. */
  tone: string;
  /** The glyph's colour where it stands alone, beside its word. */
  ink: string;
}

interface StatusLook extends ChipLook {
  /** The status as a dot beside its word, in the list and the report's header. */
  dot: string;
}

export const STATUS_LOOK: Record<FeedbackStatus, StatusLook> = {
  open: { label: 'Open', Icon: InboxIcon, tone: 'bg-amber-500/15 text-amber-200 ring-amber-400/40', ink: 'text-amber-300', dot: 'bg-amber-400' },
  in_progress: { label: 'In progress', Icon: ClockIcon, tone: 'bg-cyan-500/15 text-cyan-200 ring-cyan-400/40', ink: 'text-cyan-300', dot: 'bg-cyan-400' },
  resolved: { label: 'Resolved', Icon: CheckCircleIcon, tone: 'bg-green-500/15 text-green-200 ring-green-400/40', ink: 'text-green-300', dot: 'bg-emerald-400' },
  closed: { label: 'Closed', Icon: ArchiveBoxIcon, tone: 'bg-slate-500/20 text-slate-200 ring-slate-400/40', ink: 'text-slate-400', dot: 'bg-slate-500' },
};

/** The status as a coloured dot and its word: the word carries it, the dot only helps the eye. */
export function StatusDot({ status, className }: { status: FeedbackStatus; className?: string }) {
  const look = STATUS_LOOK[status] ?? STATUS_LOOK.open;
  return (
    <span className={clsx('inline-flex items-center gap-2 whitespace-nowrap text-slate-200', className)}>
      <span aria-hidden="true" className={clsx('h-2 w-2 shrink-0 rounded-full', look.dot)} />
      {look.label}
    </span>
  );
}

/** Severity as a word, coloured only when it needs attention (Major amber, Critical red). */
export const SEVERITY_INK: Record<FeedbackSeverity, string> = {
  critical: 'font-medium text-red-400',
  major: 'font-medium text-amber-300',
  minor: '',
};

export const CATEGORY_LOOK: Record<FeedbackCategory, { label: string; Icon: Glyph }> = {
  bug: { label: 'Bug', Icon: BugAntIcon },
  suggestion: { label: 'Suggestion', Icon: LightBulbIcon },
  question: { label: 'Question', Icon: QuestionMarkCircleIcon },
  other: { label: 'Other', Icon: ChatBubbleLeftEllipsisIcon },
};

export const SEVERITY_LOOK: Record<FeedbackSeverity, ChipLook> = {
  critical: { label: 'Critical', Icon: ExclamationTriangleIcon, tone: 'bg-red-500/15 text-red-200 ring-red-400/50', ink: 'text-red-300' },
  major: { label: 'Major', Icon: ExclamationCircleIcon, tone: 'bg-amber-500/15 text-amber-200 ring-amber-400/40', ink: 'text-amber-300' },
  minor: { label: 'Minor', Icon: MinusCircleIcon, tone: 'bg-slate-500/20 text-slate-200 ring-slate-400/40', ink: 'text-slate-400' },
};

const chipBase = 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-sm font-medium ring-1 ring-inset';

export function StatusChip({ status, className }: { status: FeedbackStatus; className?: string }) {
  const look = STATUS_LOOK[status] ?? STATUS_LOOK.open;
  return (
    <span className={clsx(chipBase, look.tone, className)}>
      <look.Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {look.label}
    </span>
  );
}

export function SeverityChip({ severity, className }: { severity: FeedbackSeverity; className?: string }) {
  const look = SEVERITY_LOOK[severity];
  if (!look) return null;
  return (
    <span className={clsx(chipBase, look.tone, className)}>
      <look.Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {look.label}
    </span>
  );
}

/** Category reads as a plain labelled glyph: it sorts reports, it is not a state. */
export function CategoryLabel({ category, className }: { category: FeedbackCategory; className?: string }) {
  const look = CATEGORY_LOOK[category] ?? CATEGORY_LOOK.other;
  return (
    <span className={clsx('inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-slate-200', className)}>
      <look.Icon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
      {look.label}
    </span>
  );
}

export function personName(person: Pick<FeedbackPerson, 'name' | 'email'> | null | undefined): string {
  if (!person) return '';
  return person.name?.trim() || person.email;
}

/** Two letters for an avatar: first and last name, else the start of the email. */
export function initials(person: Pick<FeedbackPerson, 'name' | 'email'>): string {
  const words = (person.name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return person.email.slice(0, 2).toUpperCase();
}

// Muted grounds from the portal's cool range that a person keeps on every page,
// so an owner is recognised at a glance.
const AVATAR_TONES = [
  'bg-cyan-500/25 text-cyan-100',
  'bg-indigo-500/30 text-indigo-100',
  'bg-emerald-500/25 text-emerald-100',
  'bg-sky-500/25 text-sky-100',
  'bg-slate-500/40 text-slate-100',
  'bg-teal-500/25 text-teal-100',
];

function toneFor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[h % AVATAR_TONES.length];
}

/** A person as a round badge of initials. Nobody is a dashed ring. */
export function Avatar({
  person,
  size = 'md',
  className,
}: {
  person: FeedbackPerson | null | undefined;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
}) {
  const box = size === 'xs' ? 'h-[22px] w-[22px] text-[13px]' : size === 'sm' ? 'h-7 w-7 text-[13px]' : 'h-8 w-8 text-[13px]';
  if (!person) {
    return <span aria-hidden="true" className={clsx('inline-block shrink-0 rounded-full border border-dashed border-slate-500', box, className)} />;
  }
  return (
    <span
      aria-hidden="true"
      className={clsx('inline-flex shrink-0 select-none items-center justify-center rounded-full font-semibold tracking-wide', box, toneFor(person.id), className)}
    >
      {initials(person)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Waiting on Vortex. The server decides it (row.waiting, the same rule as the
// Needs a reply tab); the row's last reply says which of the two it is.

/** Why a report is waiting on Vortex: the customer wrote last, or nobody has replied yet. */
export function waitingOn(row: Pick<FeedbackRow, 'waiting' | 'last_reply_from'>): 'customer' | 'no_reply' | null {
  if (!row.waiting) return null;
  return row.last_reply_from === 'customer' ? 'customer' : 'no_reply';
}

/** The one badge a report carries: who it waits on. A customer's reply is the louder of the two. */
export function WaitingFlag({ row, className }: { row: Pick<FeedbackRow, 'waiting' | 'last_reply_from'>; className?: string }) {
  const why = waitingOn(row);
  if (!why) return null;
  return why === 'customer' ? (
    <span className={clsx('inline-flex items-center whitespace-nowrap rounded bg-cyan-700 px-2 py-0.5 text-[13px] font-semibold text-cyan-50', className)}>
      Customer replied
    </span>
  ) : (
    <span className={clsx('inline-flex items-center whitespace-nowrap rounded border border-amber-700 px-[7px] py-px text-[13px] font-semibold text-amber-300', className)}>
      No reply yet
    </span>
  );
}

// ---------------------------------------------------------------------------
// The app a report is about, as the customer picked it on the Scout

const APP_GLYPH: Record<string, string> = {
  vortexnav: '/assets/apps/vortexnav.png',
  truehelm: '/assets/apps/truehelm.png',
  vortextide: '/assets/apps/vortextide.png',
  vortexanchor: '/assets/apps/vortexanchor.png',
};

/** The app's name as the customer saw it: the Launcher is "Home", Other is "Something else". */
export function appName(app: string | null | undefined): string | null {
  const a = app?.trim();
  if (!a) return null;
  if (a.toLowerCase() === 'launcher') return 'Home';
  if (a.toLowerCase() === 'other') return 'Something else';
  return a;
}

/** The app's glyph (as on the Scout's app tiles) and its name. Something else has no glyph. */
export function AppLabel({ app, size = 16, className }: { app: string | null | undefined; size?: 16 | 18; className?: string }) {
  const name = appName(app);
  if (!name) return null;
  const key = app!.trim().toLowerCase();
  const box = size === 18 ? 'h-[18px] w-[18px]' : 'h-4 w-4';
  return (
    <span className={clsx('inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap', className)}>
      {APP_GLYPH[key] ? (
        <img src={APP_GLYPH[key]} alt="" className={clsx('shrink-0 rounded', box)} />
      ) : key === 'launcher' ? (
        <span aria-hidden="true" className={clsx('flex shrink-0 items-center justify-center rounded bg-[#0A66FF]', box)}>
          <svg viewBox="0 0 24 24" className="h-[70%] w-[70%]"><path fill="#FFFFFF" transform="translate(0 0.5)" d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" /></svg>
        </span>
      ) : null}
      <span className="truncate">{name}</span>
    </span>
  );
}

/** Why the customer closed it, in the words they picked on the Scout. */
export const CLOSE_REASON_WORDS: Record<FeedbackCloseReason, string> = {
  working_now: 'It’s working now',
  my_mistake: 'It was my mistake',
  not_needed: 'Don’t need it now',
};

// ---------------------------------------------------------------------------
// Text

/** The first non-blank line, for one-line summaries and the report's title. */
export function firstLine(text: string | null | undefined): string {
  return (text ?? '').split('\n').map((l) => l.trim()).find(Boolean) ?? '';
}

/** Whatever follows the first non-blank line, trimmed: the report's body under its title. */
export function afterFirstLine(text: string | null | undefined): string {
  const lines = (text ?? '').split('\n');
  const i = lines.findIndex((l) => l.trim());
  return i < 0 ? '' : lines.slice(i + 1).join('\n').trim();
}

/** A report's reference, as the customer's Scout and the support emails show it: "#307456". */
export function feedbackRef(reference: number): string {
  return `#${reference}`;
}

/** Short enough for a narrow column: "22 min ago", "5 hours ago", "3 days ago", then the date. */
export function shortAgo(at: string, now = Date.now()): string {
  const mins = Math.floor((now - new Date(at).getTime()) / 60_000);
  if (!Number.isFinite(mins)) return '';
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs === 1 ? '1 hour ago' : `${hrs} hours ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return days === 1 ? '1 day ago' : `${days} days ago`;
  return formatDate(at);
}

// ---------------------------------------------------------------------------
// The list view in the address bar, so Back from a report lands on the same
// tab, filters, search and page.

/** A tab: one status, the reports waiting on Vortex, or '' for all. */
export type FeedbackTab = FeedbackStatus | 'waiting' | '';

export const FEEDBACK_TABS: readonly { value: FeedbackTab; label: string; count: keyof FeedbackCounts }[] = [
  { value: 'waiting', label: 'Needs a reply', count: 'waiting' },
  { value: 'open', label: 'Open', count: 'open' },
  { value: 'in_progress', label: 'In progress', count: 'in_progress' },
  { value: 'resolved', label: 'Resolved', count: 'resolved' },
  { value: 'closed', label: 'Closed', count: 'closed' },
  { value: '', label: 'All', count: 'all' },
];

export interface FeedbackListState {
  /** Kept as ?status= so links from before the tabs still work. */
  status: FeedbackTab;
  category: FeedbackCategory | '';
  /** '' anyone, 'me', 'none' (unassigned) or a staff user id. */
  owner: string;
  q: string;
  /** 1-based. */
  page: number;
}

export const FEEDBACK_LIST_DEFAULTS: FeedbackListState = { status: '', category: '', owner: '', q: '', page: 1 };

function oneOf<T extends string>(value: string | null, allowed: readonly T[]): T | '' {
  return value && (allowed as readonly string[]).includes(value) ? (value as T) : '';
}

export function readFeedbackListState(params: URLSearchParams): FeedbackListState {
  const page = Number(params.get('page'));
  return {
    status: oneOf(params.get('status'), [...FEEDBACK_STATUSES, 'waiting'] as const),
    category: oneOf(params.get('category'), FEEDBACK_CATEGORIES),
    owner: params.get('owner')?.trim() ?? '',
    q: params.get('q') ?? '',
    page: Number.isInteger(page) && page > 1 ? page : 1,
  };
}

/** Leaves out whatever is at its default, so the plain list has a plain address. */
export function writeFeedbackListState(state: FeedbackListState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.status) params.set('status', state.status);
  if (state.category) params.set('category', state.category);
  if (state.owner) params.set('owner', state.owner);
  if (state.q) params.set('q', state.q);
  if (state.page > 1) params.set('page', String(state.page));
  return params;
}
